import React, { useCallback, useEffect } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';

import {
    PlayerPauseIcon,
    PlayerPlayIcon,
    PlayerSkipBwdIcon,
    PlayerSkipFwdIcon,
} from 'assets/images';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import {
    PLAYER_PAUSE_BUTTON_ID,
    PLAYER_PLAY_BUTTON_ID,
    PLAYER_SKIP_BACKWARD,
    PLAYER_SKIP_FORWARD,
} from 'config/testID';

import { lightGrey } from 'utils/colors';
import { isWeb } from 'utils/responsiveness';

import {
    PlayerControlsAccessLevel,
    accessIsDisabled,
    accessIsRestricted,
} from 'screens/PlayerModal/hooks/useTrickModeControl';

import SvgButton from 'components/CustomButtons/SvgButton';

import styles from './styles';

type Props = {
    seeking: boolean;
    skipBackwardDisabled: boolean;
    skipForwardDisabled: boolean;
    pauseAccessLevel: PlayerControlsAccessLevel;
    rewindAccessLevel: PlayerControlsAccessLevel;
    fastforwardAccessLevel: PlayerControlsAccessLevel;
    showOverlay: boolean;
    isPaused: boolean;
    marginBottom?: number;
    onPlayPausePress: () => void;
    onSkipForward: () => void;
    onSkipBackward: () => void;
};

const SKIP_BWD_ID = 'skip-bwd-button';
const PLAY_PAUSE_ID = 'play-pause-button';
const SKIP_FWD_ID = 'skip-fwd-button';

const {
    ACCESSIBILITY: {
        PLAYER: {
            ACCESSIBILITY_PLAYER_PLAY,
            ACCESSIBILITY_PLAYER_PAUSE,
            ACCESSIBILITY_PLAYER_SKIPP_BACKWARDS,
            ACCESSIBILITY_PLAYER_SKIPP_FORWARD,
        },
    },
} = LANGUAGE_KEYS;

function MiddleOverlaySection({
    seeking,
    skipBackwardDisabled,
    skipForwardDisabled,
    pauseAccessLevel,
    rewindAccessLevel,
    fastforwardAccessLevel,
    showOverlay = false,
    isPaused = true,
    marginBottom,
    onPlayPausePress,
    onSkipForward,
    onSkipBackward,
}: Props) {
    const { t } = useTranslation();

    const handlePreventDoubleClickPropagation = useCallback((e: MouseEvent) => {
        e.preventDefault();
        e.stopImmediatePropagation();
    }, []);

    useEffect(() => {
        if (isWeb && showOverlay) {
            [SKIP_BWD_ID, PLAY_PAUSE_ID, SKIP_FWD_ID].forEach((elmentId) => {
                document
                    .getElementById(elmentId)
                    ?.addEventListener(
                        'dblclick',
                        handlePreventDoubleClickPropagation,
                    );
            });
        }

        return () => {
            if (isWeb) {
                [SKIP_BWD_ID, PLAY_PAUSE_ID, SKIP_FWD_ID].forEach(
                    (elmentId) => {
                        document
                            .getElementById(elmentId)
                            ?.removeEventListener(
                                'dblclick',
                                handlePreventDoubleClickPropagation,
                            );
                    },
                );
            }
        };
    }, [showOverlay]);

    return (
        <>
            {showOverlay && (
                <View
                    style={[
                        styles.triggersContainer,
                        !!marginBottom && {
                            marginBottom,
                        },
                    ]}
                    accessible={false}
                >
                    {!accessIsDisabled(rewindAccessLevel) ? (
                        <SvgButton
                            accessible
                            accessibilityRole="button"
                            accessibilityLabel={t(
                                ACCESSIBILITY_PLAYER_SKIPP_BACKWARDS,
                            )}
                            id={SKIP_BWD_ID}
                            style={styles.skipBack}
                            svgStyle={styles.skipButtons}
                            disabled={skipBackwardDisabled}
                            fill={
                                skipBackwardDisabled ||
                                accessIsRestricted(rewindAccessLevel)
                                    ? lightGrey(0.4)
                                    : lightGrey()
                            }
                            Svg={PlayerSkipBwdIcon}
                            onPress={onSkipBackward}
                            testID={PLAYER_SKIP_BACKWARD}
                        />
                    ) : (
                        <View style={styles.skipBack} />
                    )}
                    {!seeking && !accessIsDisabled(pauseAccessLevel) && (
                        <>
                            {isPaused && (
                                <SvgButton
                                    accessible
                                    accessibilityRole="button"
                                    accessibilityLabel={t(
                                        ACCESSIBILITY_PLAYER_PLAY,
                                    )}
                                    id={PLAY_PAUSE_ID}
                                    style={styles.playButton}
                                    svgStyle={styles.playSVG}
                                    fill={
                                        accessIsRestricted(pauseAccessLevel)
                                            ? lightGrey(0.4)
                                            : lightGrey()
                                    }
                                    Svg={PlayerPlayIcon}
                                    onPress={onPlayPausePress}
                                    testID={PLAYER_PLAY_BUTTON_ID}
                                />
                            )}
                            {!isPaused && (
                                <SvgButton
                                    accessible
                                    accessibilityRole="button"
                                    accessibilityLabel={t(
                                        ACCESSIBILITY_PLAYER_PAUSE,
                                    )}
                                    id={PLAY_PAUSE_ID}
                                    style={styles.pauseButton}
                                    svgStyle={styles.pauseSVG}
                                    fill={
                                        accessIsRestricted(pauseAccessLevel)
                                            ? lightGrey(0.4)
                                            : lightGrey()
                                    }
                                    Svg={PlayerPauseIcon}
                                    onPress={onPlayPausePress}
                                    testID={PLAYER_PAUSE_BUTTON_ID}
                                />
                            )}
                        </>
                    )}
                    {!accessIsDisabled(fastforwardAccessLevel) ? (
                        <SvgButton
                            accessible
                            accessibilityRole="button"
                            accessibilityLabel={t(
                                ACCESSIBILITY_PLAYER_SKIPP_FORWARD,
                            )}
                            id={SKIP_FWD_ID}
                            style={styles.skipForward}
                            svgStyle={styles.skipButtons}
                            disabled={skipForwardDisabled}
                            fill={
                                skipForwardDisabled ||
                                accessIsRestricted(fastforwardAccessLevel)
                                    ? lightGrey(0.4)
                                    : lightGrey()
                            }
                            Svg={PlayerSkipFwdIcon}
                            onPress={onSkipForward}
                            testID={PLAYER_SKIP_FORWARD}
                        />
                    ) : (
                        <View style={styles.skipForward} />
                    )}
                </View>
            )}
        </>
    );
}

export default MiddleOverlaySection;
